import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CandidateDetailModal = ({ candidate, onClose, onRevealRequest }) => {
  if (!candidate) return null;

  const getMatchColor = (score) => {
    if (score >= 80) return 'text-[#10B981]';
    if (score >= 60) return 'text-[#F59E0B]';
    return 'text-[#6B7280]';
  };

  const getMatchBgColor = (score) => {
    if (score >= 80) return 'bg-[#10B981]/10';
    if (score >= 60) return 'bg-[#F59E0B]/10';
    return 'bg-[#E5E7EB]';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] p-4 md:p-6 flex items-center justify-between">
          <h2 className="text-xl md:text-2xl font-bold text-[#111827]">Candidate Profile</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#F6F7FB] rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} color="#6B7280" />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-2xl font-semibold text-[#111827] mb-2">{candidate?.title}</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F6F7FB] rounded-full">
                  <Icon name="Briefcase" size={16} color="#6B7280" />
                  <span className="text-sm text-[#6B7280]">{candidate?.experience}</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F6F7FB] rounded-full">
                  <Icon name="MapPin" size={16} color="#6B7280" />
                  <span className="text-sm text-[#6B7280]">{candidate?.location}</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F6F7FB] rounded-full">
                  <Icon name="Building2" size={16} color="#6B7280" />
                  <span className="text-sm text-[#6B7280]">{candidate?.industry}</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F6F7FB] rounded-full">
                  <Icon name="TrendingUp" size={16} color="#6B7280" />
                  <span className="text-sm text-[#6B7280]">{candidate?.seniority}</span>
                </div>
              </div>
            </div>
            <div className={`ml-4 flex flex-col items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-xl ${getMatchBgColor(candidate?.matchScore)}`}>
              <span className={`text-2xl md:text-3xl font-bold ${getMatchColor(candidate?.matchScore)}`}>
                {candidate?.matchScore}%
              </span>
              <span className="text-xs text-[#6B7280]">{candidate?.matchAnalysis?.recommendation || 'Match'}</span>
            </div>
          </div>

          {candidate?.matchAnalysis && !candidate?.matchAnalysis?.error && (
            <div className="bg-[#F6F7FB] rounded-xl p-4 md:p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Icon name="Target" size={20} color="#5B2DFF" />
                <h4 className="text-lg font-semibold text-[#111827]">AI Match Analysis</h4>
              </div>
              <p className="text-sm text-[#6B7280]">{candidate?.matchAnalysis?.matchSummary}</p>
              
              {candidate?.matchAnalysis?.keyStrengths?.length > 0 && (
                <div>
                  <h5 className="text-sm font-semibold text-[#111827] mb-2">Key Strengths</h5>
                  <ul className="space-y-2">
                    {candidate?.matchAnalysis?.keyStrengths?.map((strength, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Icon name="CheckCircle2" size={16} color="#10B981" className="mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-[#111827]">{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {candidate?.matchAnalysis?.potentialConcerns?.length > 0 && (
                <div>
                  <h5 className="text-sm font-semibold text-[#111827] mb-2">Considerations</h5>
                  <ul className="space-y-2">
                    {candidate?.matchAnalysis?.potentialConcerns?.map((concern, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Icon name="AlertCircle" size={16} color="#F59E0B" className="mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-[#6B7280]">{concern}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="bg-[#F6F7FB] rounded-xl p-4 md:p-6">
            <h4 className="text-base md:text-lg font-semibold text-[#111827] mb-3">
              Professional Summary
            </h4>
            <p className="text-sm text-[#6B7280] leading-relaxed whitespace-pre-line">
              {candidate?.detailedSummary}
            </p>
          </div>

          <div className="bg-[#F6F7FB] rounded-xl p-4 md:p-6">
            <h4 className="text-base md:text-lg font-semibold text-[#111827] mb-3">
              Key Achievements
            </h4>
            <ul className="space-y-2">
              {candidate?.achievements?.map((achievement, index) => (
                <li key={index} className="flex items-start gap-2">
                  <Icon name="CheckCircle" size={16} color="#10B981" className="mt-1 flex-shrink-0" />
                  <span className="text-sm text-[#6B7280]">{achievement}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-[#E5E7EB]">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
              <span className="text-sm text-[#6B7280]">
                Profile Strength: <span className="font-medium text-[#111827]">{candidate?.profileStrength}%</span>
              </span>
            </div>
            <Button
              variant="default"
              iconName="Unlock"
              iconPosition="left"
              onClick={() => {
                onClose();
                onRevealRequest(candidate);
              }}
            >
              Request Identity Reveal
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateDetailModal;